import {
  CartesianGrid, Line, LineChart, ResponsiveContainer, Sankey, Tooltip, XAxis, YAxis,
} from 'recharts';
import { formatNumber, formatShortDate } from '../formats';

/**
 * One chart shape for the whole portal: a daily series over time. Colours
 * come from CSS custom properties so light/dark swap without touching this.
 */
export function SeriesChart({ points, height = 260 }: {
  points: { date: string; value: number }[];
  height?: number;
}) {
  if (!points.length) {
    return (
      <div
        className="flex items-center justify-center text-sm text-ink-500 dark:text-ink-400"
        style={{ height }}
      >
        No data for this period yet.
      </div>
    );
  }

  return (
    <ResponsiveContainer width="100%" height={height}>
      <LineChart data={points} margin={{ top: 8, right: 12, bottom: 0, left: 0 }}>
        <CartesianGrid stroke="var(--grid)" vertical={false} />
        <XAxis
          dataKey="date"
          tickFormatter={formatShortDate}
          tick={{ fill: 'var(--text-secondary)', fontSize: 11 }}
          tickLine={false}
          axisLine={{ stroke: 'var(--grid)' }}
          minTickGap={28}
        />
        <YAxis
          tickFormatter={(v: number) => formatNumber(v)}
          tick={{ fill: 'var(--text-secondary)', fontSize: 11 }}
          tickLine={false}
          axisLine={false}
          width={48}
        />
        <Tooltip
          formatter={(value) => [formatNumber(Number(value)), '']}
          labelFormatter={(label) => formatShortDate(String(label))}
          separator=""
          contentStyle={{
            background: 'var(--surface)',
            border: '1px solid var(--grid)',
            borderRadius: 8,
            color: 'var(--text-primary)',
            fontSize: 12,
          }}
        />
        <Line
          type="monotone"
          dataKey="value"
          stroke="var(--series-1)"
          strokeWidth={2}
          dot={false}
          activeDot={{ r: 3 }}
        />
      </LineChart>
    </ResponsiveContainer>
  );
}

/**
 * Where visitors go next: one ribbon per page-to-page move, thickness by
 * volume. `(entry)` is a visit's first pageview, so the leftmost column is
 * where people arrive.
 *
 * recharts already ships Sankey (verified against recharts 2.15.4 in
 * node_modules, not against its documentation), so this adds no dependency to
 * a bundle the whole portal downloads.
 *
 * The API caps the edges it returns. This one also drops any cycle it is
 * handed: a Sankey is a directed ACYCLIC graph, and real navigation is not -
 * somebody going /pricing -> / -> /pricing is ordinary behaviour that would
 * otherwise hang the layout. Dropping the back-edge loses a little truth and
 * keeps the chart; the numbers underneath it stay whole.
 */
export function FlowChart({ edges, height = 320 }: {
  edges: { from_path: string; to_path: string; total: number }[];
  height?: number;
}) {
  const nodes: string[] = [];
  const index = (name: string) => {
    const at = nodes.indexOf(name);
    return at === -1 ? nodes.push(name) - 1 : at;
  };

  const links: { source: number; target: number; value: number }[] = [];
  const reachable = new Map<number, Set<number>>();

  for (const edge of edges) {
    if (edge.from_path === edge.to_path) continue;   // a reload is not a journey
    const source = index(edge.from_path);
    const target = index(edge.to_path);
    // Would adding this edge let `target` reach `source` again?
    if (reachable.get(target)?.has(source)) continue;
    links.push({ source, target, value: edge.total });

    const ahead = reachable.get(source) ?? new Set<number>();
    ahead.add(target);
    for (const beyond of reachable.get(target) ?? []) ahead.add(beyond);
    reachable.set(source, ahead);
    for (const set of reachable.values()) {
      if (set.has(source)) for (const beyond of ahead) set.add(beyond);
    }
  }

  if (links.length < 2) {
    return (
      <div
        className="flex items-center justify-center text-sm text-ink-500 dark:text-ink-400"
        style={{ height }}
      >
        Not enough journeys yet to draw a flow.
      </div>
    );
  }

  const data = { nodes: nodes.map((name) => ({ name })), links };

  return (
    <ResponsiveContainer width="100%" height={height}>
      <Sankey
        data={data}
        nodePadding={18}
        margin={{ top: 8, right: 120, bottom: 8, left: 8 }}
        link={{ stroke: 'var(--series-1)', strokeOpacity: 0.25 }}
        node={{ fill: 'var(--series-1)' }}
      >
        <Tooltip
          formatter={(value) => [formatNumber(Number(value)), '']}
          separator=""
          contentStyle={{
            background: 'var(--surface)',
            border: '1px solid var(--grid)',
            borderRadius: 8,
            color: 'var(--text-primary)',
            fontSize: 12,
          }}
        />
      </Sankey>
    </ResponsiveContainer>
  );
}
